import ContactModal from '../../../components/contact-modal/ContactModal';
import DOMPurify from 'isomorphic-dompurify';
import Divider from '../../../components/divider/Divider';
import ImageWithFallback from '../../../components/image-with-fallback/ImageWithFallback';
import logoSrcs from '../../../assets/images/logoSRCS.png';
import logoRcs from '../../../assets/images/logoRCS.jpg';
import {
  FacebookIcon,
  LinkedinIcon,
  PinterestIcon,
  TwitterIcon,
  WhatsappIcon,
} from '../../../assets';
import {
  ButtonText,
  ButtonsWrapper,
  CategoryName,
  CopyButton,
  CopyButtonText,
  Description,
  Ellipses,
  ProductDescriptionWrapper,
  ProductName,
  ProductTitleWrapper,
  Share,
  SocialBorder,
  SocialLink,
  SocialName,
  SrcsContainer,
  SrcsImage,
  SrcsLabel,
  StyledButton,
  StyledDetailsLink,
  TextWrapper,
} from './ProductDescription.style';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import { PrivacyLinks } from '../../../models/interface';
import { SocialType } from '../../../models/enum';
import { getStrapiMedia } from '../../../utils/media';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTheme } from '../../../theme';
import { calculateTextHeight } from 'corporate-utils';
import { SnackbarActions, useSnackbar } from '../../../stores/snackbar';
import { getCompany } from '../../../utils/company';
import { Companies } from '../../../models/domain/enum';
import { useIsVisible } from '../../../hooks/useIsVisible/useIsVisible';

export interface ProductDescriptionProps {
  prodName: string;
  prodDesc: string;
  prodSrcs?: string;
  prodSrcsLabel?: string;
  prodImages: string[];
  translations: any;
  productCatalog: string;
  categoryName: string;
  userCountry: string;
}

export enum ButtonType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

const ProductDescription: React.FC<{
  data: ProductDescriptionProps;
  privacyPolicy: PrivacyLinks;
  isPrerenderRequest: boolean;
}> = ({
  data,
  privacyPolicy,
  isPrerenderRequest,
}: {
  data: ProductDescriptionProps;
  privacyPolicy: PrivacyLinks;
  isPrerenderRequest: boolean;
}) => {
  const {
    prodName,
    prodDesc,
    prodSrcs,
    prodSrcsLabel,
    prodImages,
    translations,
    categoryName,
    userCountry,
  } = data || {};
  const { dispatch } = useSnackbar();
  const theme = useTheme();
  const router = useRouter();
  const { asPath, locale: localization } = router;
  const [showMoreLess, setShowMoreLess] = useState(false);
  const [moreDetail, setMoreDetail] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const shareUrl = `${process?.env?.NEXT_PUBLIC_URL}/${localization}${asPath}`;
  const descSanitized = DOMPurify.sanitize(prodDesc ?? '');
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const company = getCompany();
  const ref = useRef<Element>();
  const isVisible = useIsVisible(ref, true);

  useEffect(() => {
    const linesNumber =
      Number(process?.env?.NEXT_PUBLIC_PRODUCT_DESCRIPTION_LESS_LINES) ?? 10;
    const textHeight = calculateTextHeight(
      theme?.state?.theme?.fonts?.utility?.size(12 / 2),
      'productDescriptionText'
    );
    const textHeightThreshold =
      Number(
        theme?.state?.theme?.fonts?.utility
          ?.lineHeight(29 / 4)
          .replace('px', '')
      ) * linesNumber;
    if (!textHeight || Number.isNaN(textHeightThreshold)) return;
    setShowMoreLess(textHeight > textHeightThreshold);
    setMoreDetail(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localization, isVisible]);

  return (
    <div>
      <ProductDescriptionWrapper>
        <TextWrapper>
          <ProductTitleWrapper>
            <CategoryName>{categoryName ?? ''}</CategoryName>
            <ProductName>{prodName ?? ''}</ProductName>
            {prodSrcs === 'Y' && (
              <SrcsContainer>
                <SrcsImage>
                  <ImageWithFallback
                    isPrerenderRequest={isPrerenderRequest}
                    layout="fill"
                    objectFit="contain"
                    src={
                      company === Companies.SIRMAN
                        ? logoSrcs
                        : company === Companies.FELSINEA
                          ? logoRcs
                          : ''
                    }
                    alt={'logo srcs'}
                    fallbackSrc={DEFAULT_IMAGE}
                  />
                </SrcsImage>
                <SrcsLabel>{prodSrcsLabel}</SrcsLabel>
              </SrcsContainer>
            )}
          </ProductTitleWrapper>
          <Description
            id="productDescriptionText"
            showMoreDetail={moreDetail}
            dangerouslySetInnerHTML={{ __html: descSanitized }}
          />
          {!moreDetail && showMoreLess && <Ellipses />}
          {showMoreLess &&
            (!moreDetail ? (
              <StyledDetailsLink
                onClick={(e: any) => {
                  e?.preventDefault();
                  setMoreDetail(true);
                }}
                href={asPath}
              >
                + {translations?.pages?.productPresentation?.moreDetails}
              </StyledDetailsLink>
            ) : (
              <StyledDetailsLink
                onClick={(e: any) => {
                  e?.preventDefault();
                  setMoreDetail(false);
                }}
                href={asPath}
              >
                - {translations?.pages?.productPresentation?.lessDetails}
              </StyledDetailsLink>
            ))}
        </TextWrapper>
        <ButtonsWrapper>
          <StyledButton onClick={handleOpenModal} type={ButtonType.PRIMARY}>
            <ButtonText>
              {translations?.pages?.productPresentation?.requestInformation}
            </ButtonText>
          </StyledButton>
        </ButtonsWrapper>
        <Divider />
        <Share>
          <SocialBorder>
            <CopyButton
              onClick={() => {
                try {
                  navigator.clipboard.writeText(shareUrl);
                  dispatch({
                    type: SnackbarActions.SHOW,
                    success: true,
                    message:
                      translations?.pages?.productPresentation?.copyLinkSuccess,
                  });
                } catch (error) {
                  dispatch({
                    type: SnackbarActions.SHOW,
                    success: false,
                    message: translations?.pages?.errors?.generic,
                  });
                }
              }}
            >
              <CopyButtonText>
                {translations?.pages?.productPresentation?.copyLink}
              </CopyButtonText>
            </CopyButton>
          </SocialBorder>
          <SocialBorder>
            <SocialLink type={SocialType.FACEBOOK} url={shareUrl}>
              <SocialName lang="en">Facebook</SocialName>
              <FacebookIcon />
            </SocialLink>
          </SocialBorder>
          <SocialBorder>
            <SocialLink
              type={SocialType.WHATSAPP}
              url={shareUrl}
              title={prodName ?? ''}
            >
              <SocialName lang="en">Whatsapp</SocialName>
              <WhatsappIcon />
            </SocialLink>
          </SocialBorder>
          <SocialBorder>
            <SocialLink
              type={SocialType.LINKEDIN}
              url={shareUrl}
              title={prodName ?? ''}
              summary={prodDesc ?? ''}
            >
              <SocialName lang="en">Linkedin</SocialName>
              <LinkedinIcon />
            </SocialLink>
          </SocialBorder>
          <SocialBorder>
            <SocialLink
              type={SocialType.PINTEREST}
              url={shareUrl}
              media={getStrapiMedia(prodImages?.[0] ?? '')}
              description={prodDesc ?? ''}
            >
              <SocialName lang="en">Pinterest</SocialName>
              <PinterestIcon />
            </SocialLink>
          </SocialBorder>
          <SocialBorder>
            <SocialLink
              type={SocialType.TWITTER}
              url={shareUrl}
              title={prodName ?? ''}
            >
              <SocialName lang="en">Twitter</SocialName>
              <TwitterIcon />
            </SocialLink>
          </SocialBorder>
        </Share>
      </ProductDescriptionWrapper>
      <ContactModal
        open={isModalOpen}
        translations={translations}
        locale={localization}
        handleClose={handleCloseModal}
        privacyLink={privacyPolicy?.link ?? ''}
        privacyText={privacyPolicy?.text ?? ''}
        reason={`${translations?.pages?.productDetails?.informationReason} ${prodName}`}
        userCountry={userCountry}
      />
    </div>
  );
};

export default ProductDescription;
